// src/components/LegalLayout.jsx
import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { motion } from "framer-motion";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FileText, ShieldCheck, Scale, ChevronRight, Trash2 } from "lucide-react";

export default function LegalLayout({
  title,
  updatedAt = "18.12.2025",
  subtitle,
  children,
}) {
  const location = useLocation();

  const navItems = useMemo(
    () => [
      { to: "/kullanim-kosullari", label: "Kullanım Koşulları", icon: <Scale size={18} /> },
      { to: "/gizlilik", label: "Gizlilik Politikası", icon: <ShieldCheck size={18} /> },
      { to: "/kvkk", label: "KVKK", icon: <FileText size={18} /> },
      { to: "/hesap-silme", label: "Hesap Silme", icon: <Trash2 size={18} /> },
    ],
    []
  );

  const activePath = location.pathname;

  return (
    <>
      <Navbar />

      <main className="min-h-screen pt-24 sm:pt-28 pb-16 bg-gradient-to-b from-[#f9fafb] via-white to-[#f1f1f1]">
        <div className="max-w-5xl mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-black underline underline-offset-4">
              Ana sayfa
            </Link>
            <ChevronRight size={16} className="text-gray-400" />
            <span className="text-gray-800 font-medium">{title}</span>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6"
          >
            {/* LEFT: İçerik */}
            <section className="lg:col-span-8">
              <div className="border-2 border-black rounded-lg bg-white p-6 sm:p-10">
                <div className="flex flex-col gap-3">
                  {/* Header chip */}
                  <div className="inline-flex items-center gap-2 self-start px-3 py-1 rounded-full border border-gray-200 bg-white text-gray-700 text-sm">
                    <span className="font-semibold text-black">{title}</span>
                    <span className="text-gray-400">•</span>
                    <span>Son Güncelleme: {updatedAt}</span>
                  </div>

                  {subtitle ? (
                    <p className="text-gray-700 text-base leading-relaxed">
                      {subtitle}
                    </p>
                  ) : null}
                </div>

                {/* Content */}
                <div className="mt-8 text-gray-800">
                  {children}
                </div>

                {/* Bottom actions (sade) */}
                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center border-2 border-black bg-white text-black px-5 py-3 rounded-md font-semibold hover:bg-gray-50 transition"
                  >
                    Ana sayfaya dön
                  </Link>
                </div>
              </div>
            </section>

            {/* RIGHT: Hızlı Linkler */}
            <aside className="lg:col-span-4">
              <div className="border-2 border-black rounded-lg bg-white p-6">
                <h3 className="text-lg font-extrabold text-black">
                  Hızlı Linkler
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  İlgili metinlere hızlıca geçiş yap.
                </p>

                <div className="mt-5 flex flex-col gap-3">
                  {navItems.map((item) => {
                    const isActive = activePath === item.to;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={[
                          "flex items-center gap-3 rounded-md px-4 py-3 border transition",
                          isActive
                            ? "border-black bg-gray-50"
                            : "border-gray-200 bg-white hover:bg-gray-50",
                        ].join(" ")}
                      >
                        <div className="text-black">{item.icon}</div>
                        <div className="flex-1">
                          <div className="text-black font-semibold">
                            {item.label}
                          </div>
                          <div className="text-gray-600 text-xs mt-0.5">
                            Açmak için tıkla
                          </div>
                        </div>
                        <div className="text-black opacity-60">→</div>
                      </Link>
                    );
                  })}
                </div>

                <div className="mt-6 border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600">
                    Bir sorun mu var?{" "}
                    <a
                      href="mailto:info@kolayyuk.com"
                      className="text-black underline underline-offset-4"
                    >
                      info@kolayyuk.com
                    </a>
                  </p>
                </div>
              </div>
            </aside>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}